import {createContext, useContext, useState, useEffect, useRef, useCallback} from 'react';
import secureLocalStorage from 'react-secure-storage';
import {useNavigate} from 'react-router-dom';
import {confirmMessage} from "./Apis";
import {text_en} from '../assets/texts/en';
import {useMediaQuery} from 'react-responsive';

const AppContext = createContext();

export const AppProvider = ({children}) => {
    const [projectData, setProjectData] = useState(text_en);
    const [brandname] = useState("Maze");
    const [username] = useState("Me");
    const [messages, setMessages] = useState([]);
    const [waitlistEmail, setWaitlistEmail] = useState("");
    const [waitlistResult, setWaitlistResult] = useState(null);
    const [waitlistError, setWaitlistError] = useState(null);
    const [activeSection, setActiveSection] = useState("top");
    const [activePage, setActivePage] = useState("home");
    const isExtraLargeScreen = useMediaQuery({minWidth: 1400});
    const isLargeScreen = useMediaQuery({maxWidth: 1399, minWidth: 1100});
    const isMediumScreen = useMediaQuery({maxWidth: 1099, minWidth: 840});
    const isSmallScreen = useMediaQuery({maxWidth: 839});
    const sectionRefs = useRef({});
    const activeSectionRef = useRef(activeSection);
    const scrollContainerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const chatInputRef = useRef(null);
    const promptInputRef = useRef(null);
    const navigate = useNavigate();
    const userData = secureLocalStorage.getItem("userData");

    useEffect(() => {
        activeSectionRef.current = activeSection;
    }, [activeSection]);

    useEffect(() => {
        const scrollElement = scrollContainerRef.current;
        if (!scrollElement) {
            return;
        } 
        const handleScroll = () => {
            const containerScrollTop = scrollElement.scrollTop;
            const sectionIds = Object.keys(sectionRefs.current);
            let currentActiveSection = null;
            let minDistanceToOffset = Infinity;
            const offset = 300;
            for (let id of sectionIds) {
                const sectionElement = sectionRefs.current[id];
                if (sectionElement) {
                    const sectionTopRelativeToContainer = sectionElement.offsetTop; 
                    if (sectionTopRelativeToContainer <= containerScrollTop + offset) {
                        const distance = Math.abs(sectionTopRelativeToContainer - (containerScrollTop + offset));
                        if (distance < minDistanceToOffset) {
                            minDistanceToOffset = distance;
                            currentActiveSection = id;
                        }
                    }
                }
            }
            if (currentActiveSection && currentActiveSection !== activeSectionRef.current) {
                setActiveSection(currentActiveSection)
            }
        };
        scrollElement.addEventListener('scroll', handleScroll, {passive: true});
        return () => {
            scrollElement.removeEventListener('scroll', handleScroll);
        };
    }, [activeSection, setActiveSection]); 

    // --- Breakpoints & MiniChat --- //
    const screenSize = isLargeScreen? "-large" : isMediumScreen? "-medium" : isSmallScreen? "-small" : "";

    

    // Joining Waitlist //
    const handleSetWaitlistEmail = (email) => {
        setWaitlistEmail(email);
        setWaitlistError(null);
        setWaitlistResult(null);
    }

    const handleJoinWaitlist = async (email) => {
        if (email.trim() === "") {
            setWaitlistError("Please enter your email");
            return;
        };
        const waitlistResponse = await confirmMessage(userData.uid, waitlistEmail);
        if (waitlistResponse.error || waitlistResponse.message === "There's been a request error.") {
            setWaitlistError("There was an error. Please try again.");
            setTimeout(() => {setWaitlistError("")}, 3000);
            return;
        }
        if (waitlistResponse === true) {
            setWaitlistResult(true)
            setTimeout(() => {setWaitlistResult(null)}, 3000);
        } else if (waitlistResponse === false) {
            setWaitlistResult(false);
            setTimeout(() => {setWaitlistResult(null)}, 3000);
        }
    };

    // --- Scroll Handlers --- //
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
        }
    };

    const handleSectionClick = useCallback((id) => {
        navigate("/");
        setActivePage("home")
        setTimeout(() => {
            const targetSection = sectionRefs.current[id];
            if (targetSection) {
            targetSection.scrollIntoView({behavior: 'smooth', block: 'start'});
        };
        }, 50)
        setTimeout(() => {setActiveSection(id)}, 1200)
    }, []); // eslint-disable-line

    // ----- Context Values ---- //
    const contextValue = {
        projectData, setProjectData,
        screenSize,
        brandname, username,
        messages, setMessages,
        waitlistEmail,
        waitlistResult,
        waitlistError,
        isExtraLargeScreen, isLargeScreen, isMediumScreen, isSmallScreen,
        handleSectionClick,
        sectionRefs,
        activeSection, setActiveSection,
        activePage, setActivePage,
        scrollContainerRef,
        chatInputRef,
        promptInputRef,
        userData,
        scrollToBottom,
        handleSetWaitlistEmail,
        handleJoinWaitlist
    };

    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    );
    
};

// ----- Custom Hook ----- //
export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useAppContext must be used within a ContextProvider");
    }
    return context;
};
